import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';

const GOLD = '#c8a84b';
const DARK = '#0f1a2b';
const BORDER = 'rgba(255,255,255,0.12)';

type Step = 'type' | 'details' | 'account' | 'done';
const STEPS: Step[] = ['type', 'details', 'account', 'done'];
// Whether Supabase requires email confirmation (false — email confirm is OFF)
const EMAIL_CONFIRM_REQUIRED = false;

const getUseCases = (t: any) => [
    { key: 'bible_study', label: t('church.register.useCases.bibleStudy') },
    { key: 'youth', label: t('church.register.useCases.youth') },
    { key: 'adult_edu', label: t('church.register.useCases.adultEdu') },
    { key: 'leadership', label: t('church.register.useCases.leadership') },
];

const slideVariants = {
    enter: { opacity: 0, x: 40 },
    center: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -40 },
};

function DarkField({ label, value, onChange, placeholder, type = 'text', required = false, hint }: {
    label: string; value: string; onChange: (v: string) => void;
    placeholder?: string; type?: string; required?: boolean; hint?: string;
}) {
    const [focused, setFocused] = useState(false);
    return (
        <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#cccccc', marginBottom: 6 }}>{label}</label>
            <input
                type={type} value={value} onChange={e => onChange(e.target.value)}
                placeholder={placeholder} required={required}
                onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
                style={{
                    width: '100%', padding: '12px 16px', boxSizing: 'border-box',
                    border: `2px solid ${focused ? GOLD : 'rgba(255,255,255,0.12)'}`,
                    borderRadius: 10, fontSize: 15,
                    background: 'rgba(255,255,255,0.04)', color: '#fff',
                    outline: 'none', transition: 'border-color 0.15s',
                }}
            />
            {hint && <div style={{ fontSize: 12, color: '#999999', marginTop: 4 }}>{hint}</div>}
        </div>
    );
}

export default function ChurchRegisterPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [step, setStep] = useState<Step>('type');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [regType, setRegType] = useState<'church' | 'individual'>('church');

    useEffect(() => {
        const typeParam = searchParams.get('type');
        if (typeParam === 'individual') {
            handleTypeSubmit('individual');
        } else if (typeParam === 'church') {
            handleTypeSubmit('church');
        }
    }, [searchParams]);
    const [churchName, setChurchName] = useState('');
    const [denomination, setDenomination] = useState('');
    const [city, setCity] = useState('');
    const [country, setCountry] = useState('AU');
    const [contactName, setContactName] = useState('');
    const [contactEmail, setContactEmail] = useState('');
    const [expectedSeats, setExpectedSeats] = useState('1');
    const [useCaseFlags, setUseCaseFlags] = useState<string[]>([]);
    const [password, setPassword] = useState('');
    const [seatPrice, setSeatPrice] = useState<number>(7); // dynamic from DB

    // Fetch plan pricing from DB
    useEffect(() => {
        (async () => {
            const { data } = await supabase
                .from('subscription_plans')
                .select('price_monthly')
                .eq('plan_key', 'sage-church-seat')
                .eq('is_active', true)
                .maybeSingle();
            if (data) {
                setSeatPrice(Number(data.price_monthly) || 7);
            }
        })();
    }, []);

    // IP-based country detection
    useEffect(() => {
        const controller = new AbortController();
        let cancelled = false;
        const lookup = async () => {
            try {
                // Primary lookup: ipapi.co
                const res = await fetch('https://ipapi.co/json/', { signal: controller.signal });
                const json = await res.json();
                if (!cancelled && json?.country) {
                    const code = String(json.country).toUpperCase().slice(0, 2);
                    // Ensure it's one of our supported codes or OTHER
                    if (['AU', 'GB', 'US', 'CA', 'NZ', 'ZA', 'NG'].includes(code)) {
                        setCountry(code);
                    } else {
                        setCountry('OTHER');
                    }
                }
            } catch (err: any) {
                if (err?.name === 'AbortError') return;
                try {
                    // Fallback lookup: ipinfo.io
                    const fallbackRes = await fetch('https://ipinfo.io/json', { signal: controller.signal });
                    const fallbackJson = await fallbackRes.json();
                    if (!cancelled && fallbackJson?.country) {
                        const code = String(fallbackJson.country).toUpperCase().slice(0, 2);
                        if (['AU', 'GB', 'US', 'CA', 'NZ', 'ZA', 'NG'].includes(code)) {
                            setCountry(code);
                        } else {
                            setCountry('OTHER');
                        }
                    }
                } catch { }
            }
        };
        lookup();
        return () => { cancelled = true; controller.abort(); };
    }, []);

    const toggleUseCase = (key: string) => setUseCaseFlags(prev => prev.includes(key) ? prev.filter(u => u !== key) : [...prev, key]);

    const seats = parseInt(expectedSeats) || (regType === 'individual' ? 1 : 10);
    const monthlyPrice = seats * seatPrice;
    const planName = regType === 'church' ? t('church.register.churchPlan') : t('church.register.individualPlan');

    const stepIdx = STEPS.indexOf(step);
    const progressPct = ((stepIdx + 1) / STEPS.length) * 100;

    async function handleTypeSubmit(type: 'church' | 'individual') {
        setRegType(type);
        if (type === 'individual') {
            setChurchName('Individual Student');
            setExpectedSeats('1');
        } else {
            setExpectedSeats('10');
        }
        setStep('details');
    }

    async function handleDetailsSubmit(e: React.FormEvent) {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            const { data: existing } = await supabase.from('users').select('id').eq('email', contactEmail).maybeSingle();
            if (existing) {
                setError(t('church.register.emailExists'));
                setLoading(false);
                return;
            }
            setStep('account');
        } catch {
            setStep('account');
        } finally { setLoading(false); }
    }

    async function handleAccountCreate(e: React.FormEvent) {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            const { data: regData, error: regError } = await supabase.functions.invoke('register-church', {
                body: {
                    reg_type: regType,
                    church_name: churchName,
                    denomination,
                    city,
                    country,
                    contact_name: contactName,
                    contact_email: contactEmail,
                    password,
                    expected_seats: seats,
                },
            });

            // Handle Edge Function errors accurately
            if (regError) {
                console.error("Invoke error details:", regError);
                // Status 409 = Conflict (Email already exists)
                if ((regError as any).status === 409 || regError.message?.includes('409') || (regError as any).context?.status === 409) {
                    throw new Error(t('church.register.emailExists'));
                }
                throw regError;
            }

            if (regData?.error) {
                if (regData.error === 'email_exists') {
                    throw new Error(t('church.register.emailExists'));
                }
                throw new Error(regData.error);
            }

            // Show success / check-email screen
            setStep('done');
        } catch (err: any) {
            console.error("Signup error:", err);
            setError(err.message || t('church.register.errorGeneric'));
        } finally { setLoading(false); }
    }

    return (
        <div style={{ fontFamily: "'Inter', system-ui, sans-serif", background: DARK, minHeight: '100vh', display: 'flex', flexDirection: 'column', color: '#fff' }}>

            {/* NAV */}
            <nav style={{ padding: '16px 40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${BORDER}` }}>
                <motion.button
                    whileHover={{ x: -2 }}
                    onClick={() => navigate('/church')}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}
                >
                    <div style={{ width: 30, height: 30, background: GOLD, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ color: DARK, fontWeight: 'bold', fontSize: 15 }}>S</span>
                    </div>
                    <span style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>Sage Church</span>
                </motion.button>

                {/* Step pills */}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {STEPS.map((s, i) => (
                        <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{
                                width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                background: i <= stepIdx ? GOLD : 'rgba(255,255,255,0.1)',
                                color: i <= stepIdx ? DARK : '#666',
                                fontSize: 13, fontWeight: 700, transition: 'background 0.3s',
                            }}>{i + 1}</div>
                            <span style={{ fontSize: 13, color: i <= stepIdx ? GOLD : '#555', fontWeight: i === stepIdx ? 700 : 400 }}>{(t('church.register.stepLabels', { returnObjects: true }) as string[])[i]}</span>
                            {i < STEPS.length - 1 && <span style={{ color: '#333', fontSize: 12 }}>›</span>}
                        </div>
                    ))}
                </div>
            </nav>

            {/* PROGRESS */}
            <div style={{ height: 3, background: 'rgba(255,255,255,0.08)' }}>
                <motion.div
                    animate={{ width: `${progressPct}%` }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                    style={{ height: '100%', background: GOLD, borderRadius: '0 100px 100px 0' }}
                />
            </div>

            <div style={{ flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '56px 24px 80px' }}>
                <div style={{ width: '100%', maxWidth: 560 }}>

                    {/* ERROR */}
                    <AnimatePresence>
                        {error && (
                            <motion.div
                                initial={{ opacity: 0, y: -8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                style={{ background: 'rgba(220,38,38,0.15)', border: '1px solid rgba(220,38,38,0.4)', borderRadius: 12, padding: '14px 18px', marginBottom: 24, fontSize: 14, color: '#fca5a5' }}
                            >
                                {error}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <AnimatePresence mode="wait">

                        {/* STEP 0: SELECT TYPE */}
                        {step === 'type' && (
                            <motion.div
                                key="type"
                                variants={slideVariants}
                                initial="enter"
                                animate="center"
                                exit="exit"
                                transition={{ duration: 0.3 }}
                                style={{ textAlign: 'center' }}
                            >
                                <div style={{ marginBottom: 40 }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: GOLD, letterSpacing: '1.5px', marginBottom: 12 }}>{t('common.welcome', 'WELCOME')}</div>
                                    <h1 style={{ fontSize: 40, fontWeight: 800, letterSpacing: '-1.5px', marginBottom: 16, color: '#ffffff' }}>
                                        {t('church.register.joinType.title')}
                                    </h1>
                                    <p style={{ color: '#cccccc', fontSize: 16 }}>{t('church.register.joinType.subtitle')}</p>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                                    <motion.button
                                        whileHover={{ y: -5, background: 'rgba(255,255,255,0.06)' }}
                                        onClick={() => handleTypeSubmit('church')}
                                        style={{
                                            background: 'rgba(255,255,255,0.03)', border: `2px solid ${BORDER}`,
                                            borderRadius: 24, padding: '40px 24px', cursor: 'pointer',
                                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20,
                                            textAlign: 'center', transition: 'all 0.2s'
                                        }}
                                    >
                                        <div style={{ fontSize: 48 }}>⛪</div>
                                        <div>
                                            <h3 style={{ fontSize: 20, fontWeight: 800, color: '#fff', marginBottom: 8 }}>{t('church.register.joinType.church')}</h3>
                                            <p style={{ fontSize: 13, color: '#999999', lineHeight: 1.5 }}>
                                                {t('church.register.joinType.churchDesc')}
                                            </p>
                                        </div>
                                        <div style={{ marginTop: 'auto', color: GOLD, fontWeight: 700, fontSize: 14 }}>{t('church.register.joinType.getStarted')}</div>
                                    </motion.button>

                                    <motion.button
                                        whileHover={{ y: -5, background: 'rgba(255,255,255,0.06)' }}
                                        onClick={() => handleTypeSubmit('individual')}
                                        style={{
                                            background: 'rgba(200,168,75,0.05)', border: `2px solid ${GOLD}44`,
                                            borderRadius: 24, padding: '40px 24px', cursor: 'pointer',
                                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20,
                                            textAlign: 'center', transition: 'all 0.2s'
                                        }}
                                    >
                                        <div style={{ fontSize: 48 }}>👤</div>
                                        <div>
                                            <h3 style={{ fontSize: 20, fontWeight: 800, color: GOLD, marginBottom: 8 }}>{t('church.register.joinType.individual')}</h3>
                                            <p style={{ fontSize: 13, color: '#cccccc', lineHeight: 1.5 }}>
                                                {t('church.register.joinType.individualDesc')}
                                            </p>
                                        </div>
                                        <div style={{ marginTop: 'auto', background: GOLD, color: DARK, padding: '8px 16px', borderRadius: 12, fontWeight: 700, fontSize: 14 }}>{t('church.register.joinType.joinNow')}</div>
                                    </motion.button>
                                </div>
                            </motion.div>
                        )}

                        {/* STEP 1: CHURCH DETAILS */}
                        {step === 'details' && (
                            <motion.form
                                key="details"
                                variants={slideVariants}
                                initial="enter"
                                animate="center"
                                exit="exit"
                                transition={{ duration: 0.3 }}
                                onSubmit={handleDetailsSubmit}
                            >
                                <div style={{ marginBottom: 36 }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: GOLD, letterSpacing: '1.5px', marginBottom: 12 }}>{t('common.step_of', 'STEP {{current}} OF {{total}}', { current: 2, total: 4 })}</div>
                                    <h1 style={{ fontSize: 34, fontWeight: 800, letterSpacing: '-1.5px', marginBottom: 10, color: '#ffffff' }}>
                                        {regType === 'church' ? t('church.register.title') : t('church.register.detailsTitle')}
                                    </h1>
                                    <p style={{ color: '#cccccc', fontSize: 15 }}>
                                        {regType === 'church' ? t('church.register.subtitle') : t('church.register.detailsSubtitle')}
                                    </p>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                                    {regType === 'church' && (
                                        <>
                                            <DarkField label={t('church.register.churchName')} value={churchName} onChange={setChurchName} placeholder="Grace Community Church" required />
                                            <DarkField label={t('church.register.denomination')} value={denomination} onChange={setDenomination} placeholder="Baptist, Anglican, Non-denominational…" />
                                        </>
                                    )}

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                        <DarkField label={t('church.register.city')} value={city} onChange={setCity} placeholder="Sydney" required />
                                        <div>
                                            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#cccccc', marginBottom: 6 }}>{t('church.register.country')}</label>
                                            <select value={country} onChange={e => setCountry(e.target.value)}
                                                style={{
                                                    width: '100%', padding: '12px 14px', border: `2px solid ${BORDER}`,
                                                    borderRadius: 10, fontSize: 15, background: 'rgba(255,255,255,0.08)',
                                                    color: '#fff', outline: 'none', cursor: 'pointer'
                                                }}>
                                                <option value="AU" style={{ background: '#0a0a0a', color: '#ffffff' }}>🇦🇺 Australia</option>
                                                <option value="GB" style={{ background: '#0a0a0a', color: '#ffffff' }}>🇬🇧 United Kingdom</option>
                                                <option value="US" style={{ background: '#0a0a0a', color: '#ffffff' }}>🇺🇸 United States</option>
                                                <option value="CA" style={{ background: '#0a0a0a', color: '#ffffff' }}>🇨🇦 Canada</option>
                                                <option value="NZ" style={{ background: '#111', color: '#ffffff' }}>🇳🇿 New Zealand</option>
                                                <option value="ZA" style={{ background: '#111', color: '#ffffff' }}>🇿🇦 South Africa</option>
                                                <option value="NG" style={{ background: '#111', color: '#ffffff' }}>🇳🇬 Nigeria</option>
                                                <option value="OTHER" style={{ background: '#111', color: '#ffffff' }}>🌍 Other</option>
                                            </select>
                                        </div>
                                    </div>

                                    <DarkField label={regType === 'church' ? t('church.register.contactName') : t('church.register.fullName')} value={contactName} onChange={setContactName} placeholder={regType === 'church' ? 'Pastor James Smith' : 'John Doe'} required />
                                    <DarkField label={regType === 'church' ? t('church.register.contactEmail') : t('church.register.emailAddress')} value={contactEmail} onChange={setContactEmail} placeholder={regType === 'church' ? 'james@gracechurch.org' : 'john@example.com'} type="email" required />

                                    {/* SEATS */}
                                    <div>
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#cccccc', marginBottom: 6 }}>
                                            {regType === 'church' ? t('church.register.seats') : t('church.register.seatsOptional')}
                                        </label>
                                        <input
                                            type="number" min={1} max={500} value={expectedSeats}
                                            onChange={e => setExpectedSeats(e.target.value)}
                                            style={{ width: '100%', padding: '12px 16px', border: `2px solid ${BORDER}`, borderRadius: 10, fontSize: 15, background: 'rgba(255,255,255,0.06)', color: '#fff', outline: 'none', boxSizing: 'border-box' }}
                                        />
                                        <div style={{ marginTop: 10, background: 'rgba(200,168,75,0.12)', border: '1px solid rgba(200,168,75,0.4)', borderRadius: 10, padding: '12px 16px', fontSize: 14, color: GOLD, fontWeight: 600 }}>
                                            {planName} · ${monthlyPrice.toFixed(2)}/month
                                            {` ($${seatPrice.toFixed(2)} × ${seats} seat${seats > 1 ? 's' : ''})`}
                                        </div>
                                        {regType === 'individual' && seats > 1 && (
                                            <div style={{ fontSize: 12, color: '#999999', marginTop: 8 }}>
                                                {t('church.register.seatsHint')}
                                            </div>
                                        )}
                                    </div>

                                    {/* USE CASES — only shown for church path */}
                                    {regType === 'church' && (
                                        <div>
                                            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#cccccc', marginBottom: 10 }}>
                                                {t('church.register.useCaseLabel')}
                                            </label>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                                {getUseCases(t).map(uc => (
                                                    <motion.button
                                                        key={uc.key}
                                                        type="button"
                                                        whileHover={{ scale: 1.02 }}
                                                        whileTap={{ scale: 0.97 }}
                                                        onClick={() => toggleUseCase(uc.key)}
                                                        style={{
                                                            background: useCaseFlags.includes(uc.key) ? 'rgba(200,168,75,0.2)' : 'rgba(255,255,255,0.06)',
                                                            border: `1.5px solid ${useCaseFlags.includes(uc.key) ? GOLD : BORDER}`,
                                                            borderRadius: 10, padding: '12px', cursor: 'pointer',
                                                            fontSize: 13, color: useCaseFlags.includes(uc.key) ? GOLD : '#eeeeee',
                                                            fontWeight: 600, textAlign: 'left',
                                                            transition: 'all 0.15s',
                                                        }}
                                                    >
                                                        {uc.label}
                                                    </motion.button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <motion.button
                                    type="submit"
                                    disabled={loading}
                                    whileHover={{ scale: 1.01 }}
                                    whileTap={{ scale: 0.98 }}
                                    style={{ marginTop: 32, width: '100%', background: GOLD, color: DARK, border: 'none', padding: '15px', borderRadius: 12, cursor: loading ? 'not-allowed' : 'pointer', fontSize: 16, fontWeight: 700, opacity: loading ? 0.7 : 1, boxShadow: '0 4px 20px rgba(200,168,75,0.3)' }}
                                >
                                    {loading ? t('church.register.checking') : t('church.register.continue')}
                                </motion.button>

                                <motion.button
                                    type="button"
                                    onClick={() => setStep('type')}
                                    style={{ marginTop: 12, width: '100%', background: 'none', border: `1px solid ${BORDER}`, color: '#666', padding: '12px', borderRadius: 12, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}
                                >
                                    {t('church.register.changeType')}
                                </motion.button>

                                <p style={{ textAlign: 'center', marginTop: 18, fontSize: 13, color: '#555' }}>
                                    {t('church.register.alreadyHave')}{' '}
                                    <button onClick={() => navigate('/signin')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: GOLD, fontWeight: 700 }}>
                                        {t('church.register.signIn')}
                                    </button>
                                </p>
                            </motion.form>
                        )}

                        {/* STEP 2: PASSWORD */}
                        {step === 'account' && (
                            <motion.form
                                key="account"
                                variants={slideVariants}
                                initial="enter"
                                animate="center"
                                exit="exit"
                                transition={{ duration: 0.3 }}
                                onSubmit={handleAccountCreate}
                            >
                                <div style={{ marginBottom: 36 }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: GOLD, letterSpacing: '1.5px', marginBottom: 12 }}>{t('common.step_of', 'STEP {{current}} OF {{total}}', { current: 3, total: 4 })}</div>
                                    <h1 style={{ fontSize: 34, fontWeight: 800, letterSpacing: '-1.5px', marginBottom: 10, color: '#ffffff' }}>{t('church.register.createLogin')}</h1>
                                    <p style={{ color: '#cccccc', fontSize: 15 }}>
                                        {regType === 'church' ? t('church.register.adminFor') : t('church.register.accountFor')} <strong style={{ color: GOLD }}>{regType === 'church' ? churchName : contactName}</strong>
                                    </p>
                                </div>

                                <div style={{ background: 'rgba(255,255,255,0.06)', border: `1px solid ${BORDER}`, borderRadius: 12, padding: '16px 20px', marginBottom: 20 }}>
                                    <div style={{ fontSize: 12, color: '#999999', marginBottom: 4 }}>{t('church.register.adminEmail')}</div>
                                    <div style={{ color: '#ffffff', fontWeight: 600 }}>{contactEmail}</div>
                                </div>

                                <DarkField label={t('church.register.password')} value={password} onChange={setPassword} type="password" placeholder={t('church.register.passwordPlaceholder')} required hint={t('church.register.passwordHint')} />

                                {/* ORDER SUMMARY */}
                                <div style={{ marginTop: 24, background: 'rgba(200,168,75,0.08)', border: '1px solid rgba(200,168,75,0.3)', borderRadius: 14, padding: '20px 24px' }}>
                                    <div style={{ fontWeight: 700, color: GOLD, marginBottom: 14, fontSize: 14 }}>{t('church.register.orderSummary')}</div>
                                    <div style={{ fontSize: 14, color: '#aaa', display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                                        <span>{regType === 'church' ? churchName : t('church.register.individualPath')}</span><span>{seats} seat{seats > 1 ? 's' : ''}</span>
                                    </div>
                                    <div style={{ fontSize: 14, color: '#aaa', display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                                        <span>Plan</span><span>{planName}</span>
                                    </div>
                                    <div style={{ fontSize: 14, color: '#aaa', display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                                        <span>Total</span><span>A${monthlyPrice.toFixed(0)}/month</span>
                                    </div>
                                    <div style={{ borderTop: '1px solid rgba(200,168,75,0.2)', marginTop: 12, paddingTop: 12, fontWeight: 700, color: GOLD, fontSize: 16, display: 'flex', justifyContent: 'space-between' }}>
                                        <span>{t('church.register.firstMonth')}</span><span>{t('church.register.free')}</span>
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12, marginTop: 28 }}>
                                    <motion.button
                                        type="button"
                                        whileHover={{ scale: 1.01 }}
                                        whileTap={{ scale: 0.97 }}
                                        onClick={() => setStep('details')}
                                        style={{ background: 'none', border: `2px solid ${BORDER}`, color: '#aaa', padding: '14px', borderRadius: 12, cursor: 'pointer', fontSize: 15, fontWeight: 600 }}
                                    >
                                        ← {t('church.register.back')}
                                    </motion.button>
                                    <motion.button
                                        type="submit"
                                        disabled={loading}
                                        whileHover={{ scale: 1.01 }}
                                        whileTap={{ scale: 0.98 }}
                                        style={{ background: GOLD, color: DARK, border: 'none', padding: '14px', borderRadius: 12, cursor: loading ? 'not-allowed' : 'pointer', fontSize: 15, fontWeight: 700, opacity: loading ? 0.7 : 1, boxShadow: '0 4px 20px rgba(200,168,75,0.3)' }}
                                    >
                                        {loading
                                            ? t('church.register.creating')
                                            : regType === 'church'
                                                ? t('church.register.registerBtn')
                                                : t('church.register.createAccountBtn', 'Create Account →')}
                                    </motion.button>
                                </div>
                            </motion.form>
                        )}

                        {/* STEP 3: SUCCESS / CHECK EMAIL */}
                        {step === 'done' && (
                            <motion.div
                                key="done"
                                variants={slideVariants}
                                initial="enter"
                                animate="center"
                                exit="exit"
                                transition={{ duration: 0.3 }}
                                style={{ textAlign: 'center' }}
                            >
                                <motion.div
                                    animate={{ scale: [0.8, 1.1, 1], rotate: [0, -5, 5, 0] }}
                                    transition={{ duration: 0.6 }}
                                    style={{ fontSize: 72, marginBottom: 28 }}
                                >
                                    {EMAIL_CONFIRM_REQUIRED ? '✉️' : (regType === 'church' ? '⛪' : '🎉')}
                                </motion.div>
                                <motion.h1
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.3 }}
                                    style={{ fontSize: 32, fontWeight: 800, letterSpacing: '-1px', marginBottom: 14, color: '#ffffff' }}
                                >
                                    {EMAIL_CONFIRM_REQUIRED
                                        ? t('church.register.checkEmailTitle', 'Check Your Email!')
                                        : `${regType === 'church' ? churchName : contactName} ${t('church.register.successTitle')}`}
                                </motion.h1>
                                <motion.p
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 0.5 }}
                                    style={{ color: '#ffffff', fontSize: 16, fontWeight: 500, marginBottom: 36, lineHeight: 1.8, maxWidth: 480, margin: '0 auto 36px' }}
                                >
                                    {EMAIL_CONFIRM_REQUIRED
                                        ? t('church.register.checkEmailMessage',
                                            `We've sent a confirmation link to {{email}}. Please check your inbox (and spam folder) and click the link to activate your account, then sign in.`,
                                            { email: contactEmail })
                                        : t('church.register.successMessage')}
                                </motion.p>
                                {EMAIL_CONFIRM_REQUIRED ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
                                        {/* Highlight the email address */}
                                        <div style={{
                                            background: 'rgba(200,168,75,0.12)',
                                            border: '1px solid rgba(200,168,75,0.35)',
                                            borderRadius: 12, padding: '14px 28px',
                                            fontSize: 16, fontWeight: 700, color: GOLD, marginBottom: 8,
                                        }}>
                                            {contactEmail}
                                        </div>
                                        <motion.button
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.97 }}
                                            onClick={() => navigate('/signin')}
                                            style={{ background: GOLD, color: DARK, border: 'none', padding: '16px 40px', borderRadius: 12, cursor: 'pointer', fontSize: 16, fontWeight: 700, width: '100%', maxWidth: 360, boxShadow: '0 4px 20px rgba(200,168,75,0.3)' }}
                                        >
                                            {t('church.register.goToSignIn', 'Go to Sign In →')}
                                        </motion.button>
                                        <motion.button
                                            whileHover={{ scale: 1.01 }}
                                            onClick={() => navigate('/church')}
                                            style={{ background: 'none', border: `2px solid ${BORDER}`, color: '#aaa', padding: '14px 40px', borderRadius: 12, cursor: 'pointer', fontSize: 14, width: '100%', maxWidth: 360 }}
                                        >
                                            {t('church.register.backToChurch')}
                                        </motion.button>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
                                        <motion.button
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.97 }}
                                            onClick={() => navigate(regType === 'church' ? '/church/admin' : '/dashboard')}
                                            style={{ background: GOLD, color: DARK, border: 'none', padding: '16px 40px', borderRadius: 12, cursor: 'pointer', fontSize: 16, fontWeight: 700, width: '100%', maxWidth: 360, boxShadow: '0 4px 20px rgba(200,168,75,0.3)' }}
                                        >
                                            {t('church.register.goToDashboard')} →
                                        </motion.button>
                                        <motion.button
                                            whileHover={{ scale: 1.01 }}
                                            onClick={() => navigate('/church')}
                                            style={{ background: 'none', border: `2px solid ${BORDER}`, color: '#aaa', padding: '14px 40px', borderRadius: 12, cursor: 'pointer', fontSize: 14, width: '100%', maxWidth: 360 }}
                                        >
                                            {t('church.register.backToChurch')}
                                        </motion.button>
                                    </div>
                                )}
                            </motion.div>
                        )}

                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
}